export interface ConsoleLogEntry {
  timestamp: string;
  level: 'error' | 'warn';
  message: string;
  stack?: string;
}

const MAX_LOGS = 30;
const logBuffer: ConsoleLogEntry[] = [];
let isInitialized = false;

const safeSerialize = (arg: unknown): string => {
  if (arg === null) return 'null';
  if (arg === undefined) return 'undefined';
  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ''}`;
  }
  if (typeof arg === 'object') {
    try {
      return JSON.stringify(arg);
    } catch {
      return String(arg);
    }
  }
  return String(arg);
};

export const initConsoleLogger = (): void => {
  if (isInitialized || typeof window === 'undefined') return;
  isInitialized = true;

  const originalError = console.error;
  const originalWarn = console.warn;

  console.error = (...args: unknown[]) => {
    try {
      const message = args.map(safeSerialize).join(' ');
      const errArg = args.find((a) => a instanceof Error) as Error | undefined;
      const stack = errArg?.stack;

      logBuffer.push({
        timestamp: new Date().toISOString(),
        level: 'error',
        message,
        stack,
      });

      if (logBuffer.length > MAX_LOGS) {
        logBuffer.shift();
      }
    } catch {
      // Ignore logger internal error
    }
    originalError.apply(console, args);
  };

  console.warn = (...args: unknown[]) => {
    try {
      const message = args.map(safeSerialize).join(' ');

      logBuffer.push({
        timestamp: new Date().toISOString(),
        level: 'warn',
        message,
      });

      if (logBuffer.length > MAX_LOGS) {
        logBuffer.shift();
      }
    } catch {
      // Ignore logger internal error
    }
    originalWarn.apply(console, args);
  };

  window.addEventListener('error', (event) => {
    try {
      logBuffer.push({
        timestamp: new Date().toISOString(),
        level: 'error',
        message: event.message || 'Uncaught window error',
        stack: event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`,
      });
      if (logBuffer.length > MAX_LOGS) {
        logBuffer.shift();
      }
    } catch {
      // Ignore
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason = event.reason;
      logBuffer.push({
        timestamp: new Date().toISOString(),
        level: 'error',
        message: `Unhandled Promise Rejection: ${safeSerialize(reason)}`,
        stack: reason instanceof Error ? reason.stack : undefined,
      });
      if (logBuffer.length > MAX_LOGS) {
        logBuffer.shift();
      }
    } catch {
      // Ignore
    }
  });
};

export const getConsoleLogs = (): ConsoleLogEntry[] => {
  return [...logBuffer];
};

export const clearConsoleLogs = (): void => {
  logBuffer.length = 0;
};
